import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API } from "../api";
import {
  FiEdit,
  FiPlus,
  FiCheckCircle,
  FiArrowLeft,
  FiX,
  FiMapPin,
} from "react-icons/fi";
import { toasts } from "../components/AppToast";
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Font,
  Alignment,
  List,
  Link,
  BlockQuote,
  Table as CKTable,
  TableToolbar,
  Image as CKImage,
  ImageUpload,
  ImageToolbar,
  ImageStyle,
  ImageResize,
  SimpleUploadAdapter,
  HorizontalLine,
} from "ckeditor5";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import "ckeditor5/ckeditor5.css";
import Layout from "../components/Layout";

export default function AddLocation() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const editData = routerLocation.state?.location || null;
  const isEdit = Boolean(editData);
  // SIDEBAR
  const [sideOpen, setSideOpen] = useState(true);
  const [activePage, setActivePage] = useState(
    isEdit ? "Edit Location" : "Add Location"
  );
  const [user, setUser] = useState(null);
  // FORM
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    loc_name: "",
    loc_pickupprice: "",
    loc_dropupprice: "",
    txaVouchrinfo: "",
    txaExtrainfo: "",
  });
  // AUTH
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    setUser(JSON.parse(localStorage.getItem("user") || "null"));
  }, [navigate]);
  // LOAD EDIT DATA
  useEffect(() => {
    if (!editData) {
      setForm({
        loc_name: "",
        loc_pickupprice: "",
        loc_dropupprice: "",
        txaVouchrinfo: "",
        txaExtrainfo: "",
      });
      return;
    }
    // console.log("EDIT DATA:", editData);
    setForm({
      loc_name: editData.loc_name ?? "",
      loc_pickupprice:
        editData.loc_pickupprice ??
        "",
      loc_dropupprice:
        editData.loc_dropupprice ??
        "",
      txaVouchrinfo:
        editData.loc_voucherinfo ??
        "",
      txaExtrainfo:
        editData.loc_extrainfo ??
        "",
    });
  }, [editData]);
  // NORMAL INPUT CHANGE
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  // CKEDITOR CHANGE
  const handleEditorChange = (field, data) => {
    setForm((prev) => ({
      ...prev,
      [field]: data,
    }));
  };
  // CKEDITOR CONFIG
  const editorConfig = {
    licenseKey: "GPL",
    plugins: [
      Essentials,
      Paragraph,
      Heading,
      Bold,
      Italic,
      Underline,
      Font,
      Alignment,
      List,
      Link,
      BlockQuote,
      CKTable,
      TableToolbar,
      CKImage,
      ImageUpload,
      ImageToolbar,
      ImageStyle,
      ImageResize,
      SimpleUploadAdapter,
      HorizontalLine,
    ],

    toolbar: [
      "undo",
      "redo",
      "|",
      "heading",
      "|",
      "bold",
      "italic",
      "underline",
      "|",
      "fontSize",
      "fontFamily",
      "fontColor",
      "fontBackgroundColor",
      "|",
      "alignment",
      "|",
      "bulletedList",
      "numberedList",
      "|",
      "link",
      "blockQuote",
      "insertTable",
      "horizontalLine",
    ],

    heading: {
      options: [
        {
          model: "paragraph",
          title: "Paragraph",
          class: "ck-heading_paragraph",
        },
        {
          model: "heading1",
          view: "h1",
          title: "Heading 1",
          class: "ck-heading_heading1",
        },
        {
          model: "heading2",
          view: "h2",
          title: "Heading 2",
          class: "ck-heading_heading2",
        },
        {
          model: "heading3",
          view: "h3",
          title: "Heading 3",
          class: "ck-heading_heading3",
        },
      ],
    },

    table: {
      contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
    },

    image: {
      toolbar: [
        "imageTextAlternative",
        "imageStyle:inline",
        "imageStyle:block",
        "imageStyle:side",
        "resizeImage",
      ],
    },

    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: "https://",
    },

    placeholder: "Enter information...",
  };
  // SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.loc_name.trim()) {
      toasts.warning("Please enter location name");
      return;
    }

    try {
      setSaving(true);
      const submitData = {
        loc_name: form.loc_name.trim(),
        loc_pickupprice:
          form.loc_pickupprice !== "" ? Number(form.loc_pickupprice) : 0,
        loc_dropupprice:
          form.loc_dropupprice !== "" ? Number(form.loc_dropupprice) : 0,
        txaVouchrinfo: form.txaVouchrinfo || "",
        txaExtrainfo: form.txaExtrainfo || "",
      };

      // console.log("SUBMIT DATA:", submitData);

      // UPDATE

      if (isEdit) {
        await API.put(`/locations/${editData.loc_id}`, submitData);

        toasts.success("Location updated successfully");

        setTimeout(() => {
          navigate("/manage-location");
        }, 1000);

        return;
      }

      // ADD

      await API.post("/locationsAdd", submitData);

      toasts.success("Location added successfully");
      navigate("/manage-location");

      // setTimeout(() => {
      //   navigate("/manage-location");
      // }, 50000);
    } catch (error) {
      console.error("Location save error:", error);

      toasts.error(
        error?.response?.data?.message ||
          `Failed to ${isEdit ? "update" : "add"} location`
      );
    } finally {
      setSaving(false);
    }
  };
  // CANCEL
  const handleCancel = () => {
    navigate("/manage-location");
  };
  // RENDER
  return (
    <Layout
      sideOpen={sideOpen}
      setSideOpen={setSideOpen}
      activePage={activePage}
      setActivePage={setActivePage}
      user={user}
    >
      <main className="dashboard-main">
        <div className="user-form-page">
          {/*  HEADER*/}
          <div className="user-form-header">
            <div className="d-flex align-items-center gap-2">
              <div className="form-card-icon">
                {isEdit ? <FiEdit size={24} /> : <FiMapPin size={24} />}
              </div>
              <div>
                <h2 className="um-modal-title mb-0">
                  {isEdit ? "Edit Location" : "Add New Location"}
                </h2>

                <p className="form-card-sub mb-0">
                  {isEdit
                    ? "(Update location information)"
                    : "(Create a new location)"}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="um-btn-secondary"
              onClick={handleCancel}
            >
              <FiArrowLeft />
              Back to Locations
            </button>
          </div>

          {/* FORM CARD*/}

          <div className="user-form-card">
            {/* LOCATION INFORMATION */}
            <div className="sec-div">
              <span>Location Information</span>
            </div>
            <div className="row g-4">
              {/* LOCATION NAME */}
              <div className="col-xl-4 col-lg-4 col-md-6">
                <label className="um-label">Location Name *</label>
                <input
                  type="text"
                  className="um-input"
                  name="loc_name"
                  value={form.loc_name}
                  onChange={handleChange}
                  placeholder="Enter location name"
                />
              </div>
              {/* PICKUP PRICE */}
              <div className="col-xl-4 col-lg-4 col-md-6">
                <label className="um-label">Pickup Price</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="um-input"
                  name="loc_pickupprice"
                  value={form.loc_pickupprice}
                  onChange={handleChange}
                  placeholder="Enter pickup price"
                />
              </div>
              {/* DROPUP PRICE */}
              <div className="col-xl-4 col-lg-4 col-md-6">
                <label className="um-label">Dropup Price</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="um-input"
                  name="loc_dropupprice"
                  value={form.loc_dropupprice}
                  onChange={handleChange}
                  placeholder="Enter dropup price"
                />
              </div>
            </div>

            {/*  VOUCHER INFORMATION */}
            <div className="sec-div mt-4">
              <span>Voucher Information</span>
            </div>
            <div className="row g-4">
              <div className="col-12">
                <label className="um-label">Voucher Information</label>

                <div className="ckeditor-wrapper">
                  <CKEditor
                    editor={ClassicEditor}
                    config={{
                      ...editorConfig,
                      placeholder: "Enter voucher information...",
                    }}
                    data={form.txaVouchrinfo}
                    onChange={(event, editor) => {
                      handleEditorChange("txaVouchrinfo", editor.getData());
                    }}
                  />
                </div>
              </div>
            </div>
            {/*  EXTRA INFORMATION */}
            <div className="sec-div mt-4">
              <span>Extra Information</span>
            </div>

            <div className="row g-4">
              <div className="col-12">
                <label className="um-label">Extra Information</label>

                <div className="ckeditor-wrapper">
                  <CKEditor
                    editor={ClassicEditor}
                    config={{
                      ...editorConfig,
                      placeholder: "Enter extra information...",
                    }}
                    data={form.txaExtrainfo}
                    onChange={(event, editor) => {
                      handleEditorChange("txaExtrainfo", editor.getData());
                    }}
                  />
                </div>
              </div>
            </div>

            {/* FORM FOOTER */}
            <div className="user-form-footer">
              <button
                type="button"
                className="um-btn-secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                <FiX />
                Cancel
              </button>

              <button
                type="button"
                className="um-btn-primary"
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner" />
                    Saving...
                  </>
                ) : isEdit ? (
                  <>
                    <FiCheckCircle />
                    Update Location
                  </>
                ) : (
                  <>
                    <FiPlus />
                    Add Location
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
}
